/**
 * SWR-based data fetching hooks for common API endpoints
 *
 * These hooks provide:
 * - Automatic request deduplication
 * - Caching with configurable TTL
 * - Revalidation strategies
 * - Loading and error states
 *
 * Migration Guide:
 * Before: useEffect(() => { BaseAPI.getX((res) => setData(res.data)); }, []);
 * After:  const { data, error, isLoading } = useX();
 */

import useSWR, { SWRConfiguration } from 'swr';
import { useMemo } from 'react';
import { BaseAPI } from 'src/providers/base-api';

// Default SWR configuration for all hooks
const DEFAULT_CONFIG: SWRConfiguration = {
  revalidateOnFocus: false,    // Don't refetch when window regains focus
  revalidateIfStale: true,      // Revalidate if data is stale
  revalidateOnMount: true,      // Revalidate when component mounts
  dedupingInterval: 2000,       // Dedupe requests within 2 seconds
  shouldRetryOnError: true,     // Retry on error
  errorRetryCount: 3,           // Retry up to 3 times
};

// Longer cache for relatively static data
const LONG_CACHE_CONFIG: SWRConfiguration = {
  ...DEFAULT_CONFIG,
  dedupingInterval: 60000,      // Cache for 60 seconds
  revalidateOnMount: true,      // Always refetch on mount to ensure fresh data
};

/**
 * Generic fetcher for GET requests via BaseAPI
 */
const createFetcher = <T>(apiFn: (callback: any) => Promise<any>) => async (): Promise<T> => {
  const response = await apiFn.call(BaseAPI, () => {});
  // Accept both 200 (OK) and 304 (Not Modified) as successful responses
  if ((response?.status === 200 || response?.status === 304) && response?.data) {
    return response.data;
  }
  throw new Error(response?.message || 'API request failed');
};

/**
 * Generic fetcher for GET requests with ID parameter
 */
const createFetcherWithId = <T>(apiFn: (id: any, callback: any) => Promise<any>) =>
  async (key: string, id: string | number): Promise<T> => {
    const response = await apiFn.call(BaseAPI, id, () => {});
    // Accept both 200 (OK) and 304 (Not Modified) as successful responses
    if ((response?.status === 200 || response?.status === 304) && response?.data) {
      return response.data;
    }
    throw new Error(response?.message || 'API request failed');
  };

// ============================================================================
// SHARED LOOKUP HOOKS
// ============================================================================

export interface EmployeeOption {
  id: number;
  partyId: number;
  firstName: string;
  lastName: string;
  employeeId?: string;
  /** Distinguishes employees who share the same `employeeId` (e.g. duplicate "0001" across companies). Omitted when empty. */
  prefixEmployeeId?: string;
  department?: string;
  /** false = inactive/former employee. Only populated when the lookup includes inactive employees (see `includeInactive`). */
  isActive?: boolean;
}

/**
 * Options for useEmployeeLookup. Kept separate from SWRConfiguration so callers
 * can pass either or both without ambiguity.
 */
export interface EmployeeLookupOptions {
  /**
   * When true, the lookup returns BOTH active and inactive employees instead of
   * the default active-only set. Opt-in only — every existing caller that omits
   * this keeps today's active-only behaviour unchanged.
   *
   * Use for flows that must be able to find former employees (e.g. mapping a
   * device PIN to someone who has since left) — never as the default, since
   * most pickers (task assignee, workflow templates, etc.) should not surface
   * former employees.
   */
  includeInactive?: boolean;
}

/**
 * Fetches lightweight employee data and returns a name lookup map
 * Uses 'employee-lookup' cache key shared with EmployeeAutocomplete.
 *
 * Cache key is parameterized by `includeInactive` so the active-only set
 * (used by every existing consumer) and the active+inactive set (opt-in) are
 * cached separately and never collide.
 */
export function useEmployeeLookup(options?: EmployeeLookupOptions, config?: SWRConfiguration) {
  const includeInactive = options?.includeInactive ?? false;
  const { data: employees = [], isLoading, error } = useSWR<EmployeeOption[]>(
    includeInactive ? 'employee-lookup-all' : 'employee-lookup',
    async () => {
      if (!includeInactive) {
        const response = await BaseAPI.getEmployeeLookup({ active: true, limit: 1000 });
        return response?.data || [];
      }
      // GET /employee/lookup supports active=all, returning active AND
      // inactive employees in one request — no client-side merge needed.
      const response = await BaseAPI.getEmployeeLookup({ active: 'all', limit: 1000 });
      return response?.data || [];
    },
    {
      dedupingInterval: 60000,
      revalidateOnFocus: false,
      ...config
    }
  );

  const nameMap = useMemo(() => {
    const map: Record<number, string> = {};
    employees.forEach(emp => {
      map[emp.partyId] = `${emp.firstName} ${emp.lastName}`.trim();
    });
    return map;
  }, [employees]);

  const employeeNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    employees.forEach(emp => {
      map[emp.id] = `${emp.firstName} ${emp.lastName}`.trim();
    });
    return map;
  }, [employees]);

  return { employees, nameMap, employeeNameMap, isLoading, error };
}

// ============================================================================
// EMPLOYEE DATA HOOKS
// ============================================================================

/**
 * Fetches all employee/personal information
 * Usage: const { data: employees, error, isLoading } = useEmployees();
 */
export function useEmployees(config?: SWRConfiguration) {
  return useSWR<any[]>(
    '/api/employees',
    createFetcher<any[]>(BaseAPI.getPersonalInformation),
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches employee by party ID
 * Usage: const { data: employee, error, isLoading } = useEmployeeByPartyId(partyId);
 */
export function useEmployeeByPartyId(partyId: number | null, config?: SWRConfiguration) {
  return useSWR(
    partyId ? ['/api/employee/party', partyId] : null,
    createFetcherWithId(BaseAPI.getEmployeeByPartyID),
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches single employee by ID
 * Usage: const { data: employee, error, isLoading } = useEmployeeById(employeeId);
 */
export function useEmployeeById(employeeId: number | null, config?: SWRConfiguration) {
  return useSWR(
    employeeId ? ['/api/employee', employeeId] : null,
    createFetcherWithId(BaseAPI.getPersonalInformationID),
    { ...DEFAULT_CONFIG, ...config }
  );
}

// ============================================================================
// USER DATA HOOKS
// ============================================================================

/**
 * Fetches all users (party details)
 * Usage: const { data: users, error, isLoading } = useUsers();
 */
export function useUsers(config?: SWRConfiguration) {
  return useSWR<any[]>(
    '/api/users',
    createFetcher<any[]>(BaseAPI.getPartyDetails),
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches user by ID
 * Usage: const { data: user, error, isLoading } = useUserById(userId);
 */
export function useUserById(userId: number | null, config?: SWRConfiguration) {
  return useSWR(
    userId ? ['/api/users', userId] : null,
    createFetcherWithId(BaseAPI.getUserByID),
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches party details by ID
 * Usage: const { data: party, error, isLoading } = usePartyDetailsById(partyId);
 */
export function usePartyDetailsById(partyId: number | null, config?: SWRConfiguration) {
  return useSWR(
    partyId ? ['/api/party', partyId] : null,
    createFetcherWithId(BaseAPI.getPartyDetailsById),
    { ...DEFAULT_CONFIG, ...config }
  );
}

// ============================================================================
// LEAVE DATA HOOKS
// ============================================================================

/**
 * Fetches all leave types
 * Usage: const { data: leaveTypes, error, isLoading } = useLeaveTypes();
 */
export function useLeaveTypes(config?: SWRConfiguration) {
  return useSWR<any[]>(
    '/api/leave-types',
    createFetcher<any[]>(BaseAPI.getLeaveTypeListData),
    { ...DEFAULT_CONFIG, ...config } // Use DEFAULT_CONFIG to always revalidate on mount
  );
}

/**
 * Fetches requested leave balance for user and leave type
 * Usage: const { data: balance, error, isLoading } = useRequestedLeaveBalance(userId, leaveTypeId);
 */
export function useRequestedLeaveBalance(
  userId: number | null,
  leaveTypeId: number | null,
  config?: SWRConfiguration
) {
  return useSWR(
    userId && leaveTypeId ? ['/api/leave-balance', userId, leaveTypeId] : null,
    async (key, uid, ltid) => {
      const response = await BaseAPI.getRequestedLeaveBalance(uid, ltid, () => {});
      if (response?.status === 200 && response?.data) {
        return response.data;
      }
      throw new Error(response?.message || 'Failed to fetch leave balance');
    },
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches user leave balances
 * Usage: const { data: balances, error, isLoading } = useUserLeaveBalances(userId);
 */
export function useUserLeaveBalances(userId: number | null, config?: SWRConfiguration) {
  return useSWR(
    userId ? ['/api/user-leave-balances', userId] : null,
    createFetcherWithId(BaseAPI.getUserLeaveBalances),
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches leave data by user
 * Usage: const { data: leaves, error, isLoading } = useLeaveDataByUser(userId);
 */
export function useLeaveDataByUser(userId: number | null, config?: SWRConfiguration) {
  return useSWR(
    userId ? ['/api/leave-data', userId] : null,
    createFetcherWithId(BaseAPI.getLeaveDataByUser),
    { ...DEFAULT_CONFIG, ...config }
  );
}

// ============================================================================
// ORGANIZATIONAL DATA HOOKS (Relatively static - longer cache)
// ============================================================================

/**
 * Fetches all roles
 * Usage: const { data: roles, error, isLoading } = useRoles();
 */
export function useRoles(config?: SWRConfiguration) {
  return useSWR<any[]>(
    '/api/roles',
    createFetcher<any[]>(BaseAPI.getRoles),
    { ...LONG_CACHE_CONFIG, ...config }
  );
}

/**
 * Fetches all departments
 * Usage: const { data: departments, error, isLoading } = useDepartments();
 */
export function useDepartments(config?: SWRConfiguration) {
  return useSWR<any[]>(
    '/api/departments',
    createFetcher<any[]>(BaseAPI.getDepartmentList),
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches all designations
 * Usage: const { data: designations, error, isLoading } = useDesignations();
 */
export function useDesignations(config?: SWRConfiguration) {
  return useSWR(
    '/api/designations',
    createFetcher(BaseAPI.getDesignationList),
    { ...LONG_CACHE_CONFIG, ...config }
  );
}

/**
 * Fetches all (non-deleted) work locations — the SAME source Daily
 * Attendance uses (BaseAPI.getWorkLocationList), so the two pages' work
 * location filters can never disagree about what "the tenant's locations"
 * are.
 * Usage: const { data: workLocations, error, isLoading } = useWorkLocationList();
 */
export function useWorkLocationList(config?: SWRConfiguration) {
  return useSWR<any[]>(
    '/api/work-locations',
    async () => {
      const response = await BaseAPI.getWorkLocationList(() => {});
      if ((response?.status === 200 || response?.status === 304) && response?.data) {
        const list = Array.isArray(response.data) ? response.data : [];
        return list.filter((item: any) => item?.deleted !== true);
      }
      throw new Error(response?.message || 'Failed to load work locations');
    },
    { ...LONG_CACHE_CONFIG, ...config }
  );
}

/**
 * Fetches job details by ID
 * Usage: const { data: jobDetails, error, isLoading } = useJobDetails(jobId);
 */
export function useJobDetails(jobId: string | null, config?: SWRConfiguration) {
  return useSWR(
    jobId ? ['/api/job-details', jobId] : null,
    createFetcherWithId(BaseAPI.getJobDetails),
    { ...DEFAULT_CONFIG, ...config }
  );
}

// ============================================================================
// ASSET DATA HOOKS
// ============================================================================

/**
 * Fetches all asset details
 * Usage: const { data: assets, error, isLoading } = useAssets();
 */
export function useAssets(config?: SWRConfiguration) {
  return useSWR(
    '/api/assets',
    createFetcher(BaseAPI.getAllAssetDetails),
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches signed URL for an asset
 * Usage: const { data: assetUrl, error, isLoading } = useAssetSignedUrl(assetId);
 *
 * Note: Asset URLs expire, so this has a shorter cache time
 */
export function useAssetSignedUrl(assetId: string | null, config?: SWRConfiguration) {
  return useSWR(
    assetId ? ['/api/asset-signed-url', assetId] : null,
    async (key, id) => {
      const result = await BaseAPI.getAssetSignedUrl(id);
      if (result) {
        return result;
      }
      throw new Error('Failed to fetch asset signed URL');
    },
    {
      ...DEFAULT_CONFIG,
      dedupingInterval: 30000, // Shorter cache (30s) - URLs expire
      ...config,
    }
  );
}

// ============================================================================
// CLIENT DATA HOOKS
// ============================================================================

/**
 * Fetches client data
 * Usage: const { data: clients, error, isLoading } = useClients();
 */
export function useClients(config?: SWRConfiguration) {
  return useSWR<any[]>(
    '/api/clients',
    createFetcher<any[]>(BaseAPI.getClientData),
    { ...LONG_CACHE_CONFIG, ...config }
  );
}

/**
 * Fetches lightweight client data for dropdowns (id, clientName, isActive, country, emailId)
 * Usage: const { data: clients, error, isLoading } = useClientsLookup();
 */
export function useClientsLookup(enabled = true, config?: SWRConfiguration) {
  return useSWR<any[]>(
    enabled ? '/api/v1/clients/lookup' : null,
    createFetcher<any[]>(BaseAPI.getClientsLookup),
    { ...LONG_CACHE_CONFIG, ...config }
  );
}

/**
 * Fetches project data for dropdowns
 * Usage: const { data: projects, error, isLoading } = useProjects();
 */
export function useProjects(config?: SWRConfiguration) {
  return useSWR<any[]>(
    '/api/projects',
    createFetcher<any[]>(BaseAPI.getAllProjectsList),
    { ...LONG_CACHE_CONFIG, ...config }
  );
}

// ============================================================================
// SETTINGS HOOKS
// ============================================================================

/**
 * Fetches settings details
 * Usage: const { data: settings, error, isLoading } = useSettings();
 */
export function useSettings(config?: SWRConfiguration) {
  return useSWR(
    '/api/settings',
    createFetcher(BaseAPI.getSettingsDetails),
    { ...LONG_CACHE_CONFIG, ...config }
  );
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Conditional hook that can be toggled on/off
 * Usage: const { data, error, isLoading } = useConditional(userId, useUserById, shouldFetch);
 */
export function useConditional<T>(
  param: any,
  hook: (param: any, config?: SWRConfiguration) => any,
  condition: boolean,
  config?: SWRConfiguration
) {
  const result = hook(condition ? param : null, config);
  return result;
}

/**
 * Hook for prefetching data
 * Usage: usePrefetch('/api/employees', BaseAPI.getPersonalInformation);
 *
 * This starts fetching data in the background without subscribing to updates
 */
export function usePrefetch(key: string, fetcher: () => Promise<any>) {
  useSWR(key, fetcher, {
    revalidateOnMount: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
  });
}

// ============================================================================
// ADMIN DEVICE MANAGEMENT HOOKS
// ============================================================================

/**
 * Fetches admin device list with optional filtering
 * Usage: const { data, error, isLoading, mutate } = useAdminDevices({ deviceType: 'face_kiosk' });
 */
export function useAdminDevices(params?: { deviceType?: string; search?: string }, config?: SWRConfiguration) {
  const queryParams: Record<string, string> = {};
  if (params?.deviceType) queryParams.deviceType = params.deviceType;
  if (params?.search) queryParams.search = params.search;
  const queryStr = Object.keys(queryParams).length > 0 ? '?' + new URLSearchParams(queryParams).toString() : '';

  return useSWR<any>(
    `/api/admin/devices${queryStr}`,
    async () => {
      const response = await BaseAPI.getAdminDevices(
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
        () => {}
      );
      if ((response?.status === 200 || response?.status === 304) && response?.data) {
        // Go backend wraps list in { statusCode, message, data: [...] }
        return Array.isArray(response.data) ? response.data : response.data?.data ?? response.data;
      }
      throw new Error(response?.message || 'Failed to load devices');
    },
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches the unresolved PIN worklist for Attendance v2 Phase 0 mapping.
 * A response status of 403 is surfaced as a distinguishable "FORBIDDEN"
 * error message so callers can render a permission-specific state (the
 * endpoints sit behind a dedicated Casbin resource, not Device Management's).
 * Usage: const { data, error, isLoading, mutate } = useUnresolvedEnrolments();
 */
export function useUnresolvedEnrolments(config?: SWRConfiguration) {
  return useSWR<any[]>(
    '/api/v1/attendance/enrolments/unresolved',
    async () => {
      const response = await BaseAPI.getUnresolvedEnrolments(() => {});
      if ((response?.status === 200 || response?.status === 304) && response?.data) {
        return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
      }
      if (response?.status === 403) {
        throw new Error('FORBIDDEN');
      }
      throw new Error(response?.message || 'Failed to load unresolved enrolments');
    },
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches confirmed PIN → employee enrolment mappings, optionally filtered.
 * Usage: const { data, error, isLoading, mutate } = useEnrolments();
 */
export function useEnrolments(params?: { devicePin?: string; employeeId?: string | number }, config?: SWRConfiguration) {
  const queryParams: Record<string, string> = {};
  if (params?.devicePin) queryParams.devicePin = params.devicePin;
  if (params?.employeeId != null) queryParams.employeeId = String(params.employeeId);
  const queryStr = Object.keys(queryParams).length > 0 ? '?' + new URLSearchParams(queryParams).toString() : '';

  return useSWR<any[]>(
    `/api/v1/attendance/enrolments${queryStr}`,
    async () => {
      const response = await BaseAPI.getEnrolments(
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
        () => {}
      );
      if ((response?.status === 200 || response?.status === 304) && response?.data) {
        return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
      }
      if (response?.status === 403) {
        throw new Error('FORBIDDEN');
      }
      throw new Error(response?.message || 'Failed to load enrolment mappings');
    },
    { ...DEFAULT_CONFIG, ...config }
  );
}

// ============================================================================
// ATTENDANCE V2 — WORKDAY SHEET (Phase 4, read-only)
// ============================================================================

export type WorkdayStatus =
  | 'scheduled'
  | 'present'
  | 'absent'
  | 'half_day'
  | 'on_leave'
  | 'wfh'
  | 'permission'
  | 'holiday'
  | 'week_off'
  | 'not_rostered';

export type WorkdayStatusLayer = 'scheduled' | 'observed' | 'declared' | 'regularized' | 'asserted';

export type WorkdayAnomaly = 'missing_out' | 'overlong' | 'out_of_window';

/** One employee-day row from GET /v1/attendance/workday (the grid). */
export interface WorkdaySheetRow {
  employeeId: number;
  workday: string; // YYYY-MM-DD
  status: WorkdayStatus;
  statusLayer: WorkdayStatusLayer;
  /** null means genuinely unknown (e.g. an IN punch with no OUT and no planned end) — NOT zero. */
  workedMinutes: number | null;
  punchCount: number;
  observedAnomaly?: WorkdayAnomaly | null;
  declaredLeaveDays: number;
  declaredPermissionMin: number;
  declaredWfh: boolean;
  /**
   * Phase 4 (Today tab) additions — landing alongside this frontend change,
   * so treated defensively: `actualStart`/`actualEnd` are omitted (not
   * present as keys) when there is no recorded punch, never a fabricated
   * time. RFC3339. `computedAt` is meant to always be present once the
   * backend change ships, but is typed optional here too so an older/cached
   * payload doesn't crash the Today tab's freshness line — see
   * `TodayTab.tsx`, which falls back to "unavailable" wording rather than a
   * fetch-time timestamp when it's missing on every row.
   */
  actualStart?: string;
  actualEnd?: string;
  computedAt?: string;

  /**
   * Shift plans v2, slice 11 (§5.4.8) — ADDITIVE and all optional: an older
   * payload, or a day computed under the legacy roster semantics, simply omits
   * them. `plannedStartLocal` / `plannedEndLocal` are "HH:MM" in the
   * employee's SITE zone (`timezone`); `plannedEndDayOffset` 1 renders as
   * "+1 day" for an overnight shift.
   */
  isScheduledWorking?: boolean;
  resolverVersion?: number;
  shiftCode?: string;
  plannedStart?: string;
  plannedEnd?: string;
  plannedStartLocal?: string;
  plannedEndLocal?: string;
  plannedEndDayOffset?: number;
  timezone?: string;
  nonWorkingReason?: string;
  /** work | off | legacy_unresolved. Omitted on a legacy day. */
  dayKind?: string;
  /** "H" | "L" | "H/L" for a legacy_unresolved day. */
  legacyCode?: string;
  /** assignment | manual (override) | swap | import | legacy. Omitted on a legacy day. */
  rosterSource?: string;
  rosterSourceId?: number;
  isHoliday?: boolean;
  holidayName?: string;
  holidayType?: string;
  /** Calendar holiday AND rostered to work it (status stays present/absent, never "holiday"). */
  workingOnHoliday?: boolean;
  isNight?: boolean;
  isRotational?: boolean;
  /** Late tile: first IN beyond the tenant grace, derived at read time. */
  isLate?: boolean;
  lateMinutes?: number;
  earlyOutMinutes?: number;
}

/** GET /v1/attendance/workday/me and /employee/:id/days (slice 12 "My attendance"). */
export interface WorkdayMyDaysResponse {
  employeeId: number;
  rollupEnabled: boolean;
  data: WorkdaySheetRow[];
}

/** One shift_change_log entry behind an employee-day (slice 11 amendment history). */
export interface WorkdayRosterChange {
  id: string;
  actorPartyId?: number;
  actorName?: string;
  at: string;
  reason: string;
  requestId?: string;
  /** assignment | exception | shift_row */
  entityKind: string;
  entityId: string;
  /** created | changed | removed */
  action: string;
  effectiveFrom: string;
  affectedFrom: string;
  affectedTo: string;
}

/** GET /v1/attendance/workday/:employeeId/:date/roster — stored Layer 1 + amendment history. */
export interface WorkdayRosterProvenance {
  employeeId: number;
  workday: string;
  resolverVersion: number;
  /** True when the day was computed under the legacy roster semantics (no v2 columns). */
  legacy: boolean;
  layer1: {
    isScheduledWorking: boolean;
    shiftCode?: string;
    plannedStart?: string;
    plannedEnd?: string;
    plannedStartLocal?: string;
    plannedEndLocal?: string;
    plannedEndDayOffset?: number;
    timezone: string;
    nonWorkingReason?: string;
    dayKind?: string;
    legacyCode?: string;
  };
  source: {
    kind: string;
    label: string;
    shiftId?: number;
    assignmentId?: string;
    exceptionId?: string;
    planId?: string;
  };
  holiday: { isHoliday?: boolean; name?: string; type?: string; workingOnHoliday: boolean };
  flags: { isNight?: boolean; isRotational?: boolean };
  history: WorkdayRosterChange[];
}

export interface WorkdayPunchPair {
  in: string;
  out?: string | null;
  /**
   * True only when `out` is the auto-complete cron's fabricated
   * startingTime+offset placeholder, never a real punch (see backend
   * derive.PunchPair.OutEstimated). Omitted (not just false) when there's no
   * `out` at all.
   */
  estimated?: boolean;
}

/** GET /v1/attendance/workday/:employeeId/:date — one day with all five layers. */
export interface WorkdayProvenance {
  employeeId: number;
  workday: string;
  status: WorkdayStatus;
  statusLayer: WorkdayStatusLayer;
  workedMinutes: number | null;
  computedAt?: string;
  /**
   * Observed envelope (earliest In, latest Out that day) — DISPLAY ONLY, a
   * "first in to last out" total-span figure alongside workedMinutes'
   * sum-of-pairs figure. RFC3339. actualEnd is omitted (not present as a
   * key) when nothing that day has a real Out yet — never fabricated.
   */
  actualStart?: string;
  actualEnd?: string;
  layers: {
    scheduled: {
      isWorking: boolean;
      isFuture: boolean;
      shiftCode?: string;
      nonWorkingReason?: string;
      plannedStart?: string | null;
      plannedEnd?: string | null;
    };
    observed: {
      punches: WorkdayPunchPair[];
      anomaly?: string;
    };
    declared: {
      leaveDays: number;
      leaveHalf?: string;
      permissionMinutes: number;
      wfh: boolean;
    };
    regularization: {
      approved: boolean;
      assertedIn?: string | null;
      assertedOut?: string | null;
    };
    mark: {
      status?: string;
      portion?: string;
      effect?: string;
    };
  };
}

/** One active employee on the grid (issue #150 — see WorkdaySheetResponse.employees). */
export interface WorkdaySheetEmployee {
  id: number;
  name: string;
  /** issue #152 — the employee's work location (branch), for client-side display/filtering. */
  workLocation?: string;
}

/**
 * GET /v1/attendance/workday response shape.
 *
 * `rollupEnabled` (issue #151) is a sibling of `data`: it distinguishes
 * "roll-up disabled for this tenant" (rollupEnabled: false, data always
 * empty) from "enabled, nothing derived yet" (rollupEnabled: true, data
 * empty) — before this fix the API could not tell these apart, and the page
 * rendered both as the same blank grid.
 *
 * `employees` (issue #150) is the tenant's active-employee roster for the
 * range, independent of which employees happen to have a derived
 * attendance_workday row — see WorkdayGrid, which now builds one row per
 * entry here rather than deriving rows purely from `data`.
 */
export interface WorkdaySheetResponse {
  data: WorkdaySheetRow[];
  employees: WorkdaySheetEmployee[];
  rollupEnabled: boolean;
}

/**
 * Fetches the Attendance v2 workday grid for a tenant + date range, optionally
 * narrowed to one employee. Pass `params: null` to skip the request entirely
 * (e.g. while the permission check is still loading, or permission is
 * absent) — avoids firing a request that the global 403 interceptor would
 * otherwise toast on.
 *
 * `attendance_workday` is only populated for tenants with the
 * `workdayRollupEnabled` flag on. Callers must render an explicit "roll-up
 * not enabled" state off `rollupEnabled`, not a blank/broken grid — see
 * WorkdaySheetResponse's doc comment.
 */
export function useAttendanceWorkdaySheet(
  params: { from: string; to: string; employeeId?: number; workLocation?: string } | null,
  config?: SWRConfiguration
) {
  return useSWR<WorkdaySheetResponse>(
    params
      ? ['/api/v1/attendance/workday', params.from, params.to, params.employeeId ?? null, params.workLocation ?? null]
      : null,
    async () => {
      const response = await BaseAPI.getAttendanceWorkdaySheet(
        { from: params!.from, to: params!.to, employeeId: params!.employeeId, workLocation: params!.workLocation },
        () => {}
      );
      if ((response?.status === 200 || response?.status === 304) && response?.data) {
        // Defensive against an older/mocked payload shape that was a bare
        // array: normalize to the current object shape rather than assuming.
        if (Array.isArray(response.data)) {
          return { data: response.data, employees: [], rollupEnabled: true };
        }
        return {
          data: response.data.data ?? [],
          employees: response.data.employees ?? [],
          rollupEnabled: response.data.rollupEnabled ?? true
        };
      }
      if (response?.status === 403) {
        throw new Error('FORBIDDEN');
      }
      throw new Error(response?.message || 'Failed to load the attendance workday sheet');
    },
    { ...DEFAULT_CONFIG, ...config }
  );
}

export interface SummaryReportColumn {
  key: string;
  label: string;
}

export interface SummaryReportCell {
  count: number;
  employees: string[] | null;
}

export interface SummaryReportRow {
  category: string;
  cells: Record<string, SummaryReportCell>;
}

export interface SummaryReportResponse {
  reportDate: string;
  viewingInstant: string;
  columns: SummaryReportColumn[];
  rows: SummaryReportRow[];
}

/**
 * Fetches the as-of-time Attendance Summary Report: a crosstab of employee
 * counts by category x work location, as of reportDate + viewingTime
 * (tenant-local). Pass `params: null` to skip the request (e.g. while the
 * form's required fields are still empty).
 */
export function useAttendanceSummaryReport(
  params: { reportDate: string; viewingTime: string; workLocationId?: number } | null,
  config?: SWRConfiguration
) {
  return useSWR<SummaryReportResponse>(
    params
      ? ['/api/v1/attendance/summary-report', params.reportDate, params.viewingTime, params.workLocationId ?? null]
      : null,
    async () => {
      const response = await BaseAPI.getAttendanceSummaryReport(
        { reportDate: params!.reportDate, viewingTime: params!.viewingTime, workLocationId: params!.workLocationId },
        () => {}
      );
      if ((response?.status === 200 || response?.status === 304) && response?.data) {
        return response.data as SummaryReportResponse;
      }
      if (response?.status === 403) {
        throw new Error('FORBIDDEN');
      }
      throw new Error(response?.message || 'Failed to load the attendance summary report');
    },
    { ...DEFAULT_CONFIG, ...config }
  );
}

/**
 * Fetches full five-layer provenance for one employee-day (design §10:
 * "provenance is one click away"). Pass `target: null` to skip the request
 * (e.g. when the provenance dialog is closed).
 */
export function useAttendanceWorkdayProvenance(
  target: { employeeId: number; date: string } | null,
  config?: SWRConfiguration
) {
  return useSWR<WorkdayProvenance>(
    target ? ['/api/v1/attendance/workday/provenance', target.employeeId, target.date] : null,
    async () => {
      const response = await BaseAPI.getAttendanceWorkdayProvenance(target!.employeeId, target!.date, () => {});
      if ((response?.status === 200 || response?.status === 304) && response?.data) {
        return response.data;
      }
      if (response?.status === 404) {
        throw new Error('NOT_COMPUTED');
      }
      if (response?.status === 403) {
        throw new Error('FORBIDDEN');
      }
      throw new Error(response?.message || 'Failed to load provenance for this day');
    },
    {
      ...DEFAULT_CONFIG,
      // Most viewers of AttendanceTab (Manager/Employee) lack
      // attendanceworkday.read and get a 403 on EVERY call — DEFAULT_CONFIG's
      // shouldRetryOnError would retry that up to 3x per tab open for an
      // endpoint that can never succeed for that role. Neither a 403 nor a
      // 404 (day outside the roll-up's range) resolves itself within a retry
      // backoff window, so there's nothing a retry buys here. Same fix, same
      // reasoning as useMyPermissionsV2 above.
      shouldRetryOnError: false,
      ...config,
    }
  );
}

// ============================================================================
// ATTENDANCE V2 — MANUAL MARKS (Phase 5, write path)
// ============================================================================

export type AttendanceMarkStatus = 'present' | 'absent';
export type AttendanceMarkPortion = 'full' | 'first_half' | 'second_half';

/**
 * Mirrors `derive.MarkEffect` (backend-go/internal/modules/attendance_workday/derive/types.go).
 * 'reverted' has no writer in the current write path — no code path in
 * usecases/marks.go ever produces it — but is a real enum value that could
 * appear on old/future data, so renderers must not choke on it.
 */
export type AttendanceMarkEffect = 'pending' | 'applied' | 'rejected' | 'superseded' | 'reverted';

/**
 * Mirrors `dto.MarkResponse` (backend-go/internal/modules/attendance_workday/dto/mark_dto.go)
 * FIELD FOR FIELD. `workday` is a plain (non-optional) string on the Go side
 * (no `omitempty`) — populated from a pgtype.Date and only ever "" if that
 * pgtype was invalid, which should not happen for a real row; treat "" as a
 * hidden-failure signal, not a valid date, rather than typing this optional.
 * Every other field below is a Go pointer with `omitempty`, meaning the KEY
 * IS OMITTED from the JSON when nil (not present as `null`) — modelled here
 * as `?:` to match.
 */
export interface AttendanceMark {
  id: number;
  employeeId: number;
  workday: string; // YYYY-MM-DD — see note above
  status: AttendanceMarkStatus;
  portion: AttendanceMarkPortion;
  reason: string;
  markedByPartyId: number;
  /**
   * Best-effort display name for `markedByPartyId`, resolved server-side.
   * Optional/absent for: older data written before this field existed, a
   * party the backend could not resolve, or a backend build that hasn't
   * deployed the resolver yet — consume defensively (fall back to a
   * neutral phrase like "a manager"), never assume presence and never
   * fall back to printing the raw party id.
   */
  markedByName?: string;
  markedAt?: string;
  effect: AttendanceMarkEffect;
  linkedRequestId?: number;
  linkedAction?: string;
  supersededBy?: number;
  reviewedBy?: number;
  /** Same defensive-consumption contract as `markedByName`, for `reviewedBy`. */
  reviewedByName?: string;
  reviewedAt?: string;
  reviewerNote?: string;
}

/** Body for POST /v1/attendance/workday/marks (dto.CreateMarkRequest). All 5 fields are required by the backend. */
export interface CreateAttendanceMarkInput {
  employeeId: number;
  workday: string; // YYYY-MM-DD
  status: AttendanceMarkStatus;
  portion: AttendanceMarkPortion;
  reason: string;
}

/** Body for .../marks/:id/approve and .../marks/:id/decline (dto.ReviewMarkRequest). Note is optional on both. */
export interface ReviewAttendanceMarkInput {
  note?: string;
}

/**
 * Fetches manual marks for a date range (+ optional employee), backing both
 * the grid's "pending mark exists" indicator (Phase 5 §"grid must indicate")
 * and the provenance dialog's per-day mark history/review panel. Gated on
 * `attendanceworkday.read` on the backend (List is a read-gate endpoint, not
 * the write gate — see mark_handler.go's own comment), so this can be called
 * whenever the sheet itself can be. Pass `params: null` to skip (dialog
 * closed, or permission not yet confirmed).
 */
export function useAttendanceWorkdayMarks(
  params: { from: string; to: string; employeeId?: number } | null,
  config?: SWRConfiguration
) {
  return useSWR<AttendanceMark[]>(
    params ? ['/api/v1/attendance/workday/marks', params.from, params.to, params.employeeId ?? null] : null,
    async () => {
      const response = await BaseAPI.getAttendanceWorkdayMarks(
        { from: params!.from, to: params!.to, employeeId: params!.employeeId },
        () => {}
      );
      if ((response?.status === 200 || response?.status === 304) && response?.data) {
        // GET .../marks wraps its array as {data: [...]} (mark_handler.go) —
        // same defensive unwrap as useAttendanceWorkdaySheet, don't assume a
        // bare array.
        return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
      }
      if (response?.status === 403) {
        throw new Error('FORBIDDEN');
      }
      throw new Error(response?.message || 'Failed to load attendance marks');
    },
    { ...DEFAULT_CONFIG, ...config }
  );
}

// ============================================================================
// V2 PERMISSIONS HOOKS
// ============================================================================

/**
 * Shape of the payload returned by GET /v1/permissions/me (aka /api/v2/permissions/me
 * — same handler, aliased under both route groups). Only the fields this file
 * consumes are typed; the endpoint also returns `entities`/`features` used by
 * `usePermissionsV2`.
 */
interface MyPermissionsV2Response {
  userId: number;
  tenantId: number;
  roles: string[];
  allPermissions: string[];
}

/**
 * Fetches the current user's flat `"resource.action"` permission list from the
 * v2 permissions endpoint (`BaseAPI.getMyPermissionsV2`). Deliberately shares
 * the SAME SWR cache key ('permissions-v2-me') that `usePermissionsV2` already
 * uses internally — that hook is called unconditionally by `usePermissions`
 * regardless of the `REACT_APP_FEATURE_NEW_RBAC` flag, so this request is
 * already in flight on any page using `usePermissions`; reusing the key just
 * dedupes onto it instead of firing a second call.
 *
 * This hook does NOT touch `usePermissionsV2`/`usePermissions` and does not
 * change the RBAC feature flag — it only reads the same underlying data via a
 * second, independent consumer.
 */
export function useMyPermissionsV2(config?: SWRConfiguration) {
  return useSWR<MyPermissionsV2Response>(
    'permissions-v2-me',
    async () => {
      const response = await BaseAPI.getMyPermissionsV2();
      if (response?.status === 200 && response?.data) {
        return response.data;
      }
      throw new Error('Failed to fetch permissions v2');
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: true,
      revalidateOnMount: true,
      dedupingInterval: 60000,
      shouldRetryOnError: false, // don't retry on 403/auth errors — prevents request storms
      ...config,
    }
  );
}

/**
 * Checks whether the current user holds a given `"resource.action"` permission
 * (e.g. `"biometricenrolment.read"`), per the flat `allPermissions` list from
 * the v2 permissions endpoint. Also honours a resource-level wildcard grant
 * (`"resource.*"`), which is how roles with blanket access to a resource (e.g.
 * Super Admin) are represented in `allPermissions` — without it, a caller
 * checking for the exact action string would incorrectly report those roles
 * as lacking the permission.
 *
 * FAILS CLOSED: any loading, error, or malformed-payload state resolves
 * `hasPermission` to `false`. This governs whether UI (e.g. a nav tab) is
 * shown, not the actual authorization decision — the backend Casbin gate on
 * the underlying API remains the real enforcement point regardless of what
 * this hook returns. But a check that fails OPEN here would flash
 * privileged UI at users who don't hold the permission whenever the
 * permissions fetch is slow, erroring, or unexpectedly shaped, so absence of
 * proof is treated as proof of absence rather than defaulting to "allow".
 *
 * Usage:
 *   const { hasPermission, isLoading } = useHasPermission('biometricenrolment.read');
 *   if (isLoading) return null; // don't flash the gated UI before we know
 *   if (!hasPermission) return null;
 */
export function useHasPermission(permission: string, config?: SWRConfiguration) {
  const { data, error, isLoading } = useMyPermissionsV2(config);

  const hasPermission = useMemo(() => {
    // Fail closed: no data yet, an error occurred, or the payload doesn't
    // have the shape we expect — never default to "allowed".
    if (error || !data || !Array.isArray(data.allPermissions)) {
      return false;
    }
    const [resource] = permission.split('.');
    const wildcard = resource ? `${resource}.*` : null;
    return data.allPermissions.includes(permission) || (wildcard != null && data.allPermissions.includes(wildcard));
  }, [data, error, permission]);

  return { hasPermission, isLoading, error };
}

/**
 * Fetches admin device stats
 * Usage: const { data: stats } = useAdminDeviceStats();
 */
export function useAdminDeviceStats(config?: SWRConfiguration) {
  return useSWR<any>(
    '/api/admin/devices/stats',
    async () => {
      const response = await BaseAPI.getAdminDeviceStats(() => {});
      if ((response?.status === 200 || response?.status === 304) && response?.data) {
        // Go backend wraps response in { statusCode, message, data }
        return response.data.data ?? response.data;
      }
      throw new Error(response?.message || 'Failed to load device stats');
    },
    { ...LONG_CACHE_CONFIG, ...config }
  );
}
